import {useGraphql} from "@/core/graphql/service";
import {useScopeQuery} from "@/core/store/scopeQuery";
import {FortunePrize, FortuneUserPrize} from "@/gql/gen";
import fortunePrizesQuery from "@/gql/gen/query/fortunePrizesQuery";
import {defineStore} from "pinia";
import {computed, ref} from "vue";
import {arrayIndex} from "@/core/util/arrayIndex";
import fortuneUserPrizeQuery from "@/gql/gen/query/fortuneUserPrizeQuery";
import fortuneSpinMutation from "@/gql/gen/mutation/fortuneSpinMutation";
import {useCatalogStore} from "@/modules/shop/store/catalog";

const STORE_NAME = "fortune";

/**
 * Pinia store для модуля Fortune Wheel
 */
export const useFortuneStore = defineStore(STORE_NAME, () => {
    const {useLazyQuery, useMutation, responseSelection} = useGraphql();
    const {registerScopeQuery} = useScopeQuery();

    const catalogStore = useCatalogStore()

    // State
    const prizes = ref<FortunePrize[]>([]);
    const userPrize = ref<FortuneUserPrize | null>(null);

    // Computed
    const prizesById = computed(() => arrayIndex(prizes.value, 'ID'))
    const userPrizeElement = computed(() => userPrize.value?.PRIZE_ELEMENT_ID ? prizesById.value[userPrize.value?.PRIZE_ELEMENT_ID] : null)
    const userPrizeGiftProductId = computed(() => userPrizeElement.value?.GIFT_ELEMENT)
    const userPrizeGiftProduct = computed(() => userPrizeGiftProductId.value ? catalogStore.productsById[userPrizeGiftProductId.value] : null)

    const gameStep = ref<'initial' | 'request' | 'spin' | 'done'>('initial')

    registerScopeQuery(
        STORE_NAME,
        "user",
        {
            fortune_prizes: {
                __fragment: "FortunePrizeFields",
            },
            fortune_user_prize: {
                __fragment: "FortuneUserPrizeFields",
            },
        },
        (data) => {
            if (data.fortune_prizes) {
                prizes.value = data.fortune_prizes
            }
            if (data.fortune_user_prize) {
                userPrize.value = data.fortune_user_prize
                if (userPrize.value) {
                    gameStep.value = 'done'
                }
            }
        }
    );

    const prizesRequest = useLazyQuery(
        fortunePrizesQuery({
            __fragment: "FortunePrizeFields",
        }),
        {
            initialResult: () => prizes.value,
            onResult: (result) => prizes.value = result,
            cache: true
        }
    );

    const userPrizesRequest = useLazyQuery(
        fortuneUserPrizeQuery({
            __fragment: "FortuneUserPrizeFields",
        }),
        {
            initialResult: () => userPrize.value,
            onResult: (result) => {
                userPrize.value = result
                if (userPrize.value) {
                    gameStep.value = 'done'
                }
            },
        }
    );

    const spinRequest = useMutation(fortuneSpinMutation({
        payload: {
            userPrize: {
                __fragment: 'FortuneUserPrizeFields'
            }
        },
        ...responseSelection()
    }), {})

    const spin = async () => {
        const res = await spinRequest.mutate({})
        if (res.success && res.payload?.userPrize) {
            userPrize.value = res.payload?.userPrize
            return userPrize.value
        }
    }

    const getPrizeIndexById = (id: number) => {
        return prizes.value.findIndex(item => item.ID === id)
    }

    return {
        // State
        gameStep,
        prizes,
        prizesById,
        userPrize,
        userPrizeElement,

        // Requests
        prizesRequest,
        userPrizesRequest,
        spinRequest,

        // Actions
        spin,
        getPrizeIndexById,

        userPrizeGiftProduct
    };
});
